/**
 * O seletor de versoes para conflito de sync (spec 5.1).
 *
 * Era a divida mais importante da fase 7: o servidor devolvia as duas versoes,
 * o cliente mantinha a operacao na fila e a UI **so avisava por toast**. Um
 * toast some em 8 s; a operacao ficava presa para sempre, voltando como
 * conflito a cada drenagem, e a escrita que a pessoa fez em modo aviao nunca
 * chegava ao banco nem era descartada por decisao de ninguem.
 *
 * Por que um aviso PERSISTENTE e nao outro toast: o conflito e raro e nao pode
 * virar aba permanente vazia, mas enquanto existe ele bloqueia uma escrita real
 * — precisa continuar alcancavel depois de trocar de tela, e sobreviver ao
 * tempo que a pessoa leva para decidir. A barra fica; o painel abre por cima.
 */
import { useEffect, useRef, useState } from 'react';
import type { SyncConflict } from '@cleartask/shared';
import { useTimeZone } from '../../session.js';
import { formatShortDate, formatTime } from '../../lib/datetime.js';

/**
 * Data e hora de um instante, para o cabecalho de cada conflito.
 *
 * Aqui o rotulo relativo do card (`formatDueLabel`, com "ontem" e "hoje") nao
 * serve: as duas linhas comparadas costumam cair no MESMO dia, e "hoje" nos
 * dois lados nao ajuda a decidir qual e mais recente. A hora e o que separa.
 */
function quando(epochMs: number, timeZone: string): string {
  return `${formatShortDate(epochMs, Date.now(), timeZone)} ${formatTime(epochMs, timeZone)}`;
}

interface ConflictPanelProps {
  conflitos: SyncConflict[];
  /** Fica com a versao enfileirada aqui (recarimba e reenvia). */
  onManterMinha: (conflito: SyncConflict) => void;
  /** Descarta a escrita local e aceita o que esta no servidor. */
  onDescartarMinha: (conflito: SyncConflict) => void;
}

export function ConflictPanel({
  conflitos,
  onManterMinha,
  onDescartarMinha,
}: ConflictPanelProps) {
  const [aberto, setAberto] = useState(false);
  const abridorRef = useRef<HTMLButtonElement>(null);

  // Resolvido o ultimo, o painel fecha sozinho: manter aberto um dialogo vazio
  // obrigaria a fechar a mao algo que ja nao tem conteudo.
  useEffect(() => {
    if (conflitos.length === 0) setAberto(false);
  }, [conflitos.length]);

  if (conflitos.length === 0) return null;

  return (
    <>
      {/*
        A barra fica ACIMA do conteudo e nao sobre a barra de abas: no celular
        a tabbar e o unico jeito de trocar de tela, e cobri-la para avisar de um
        conflito trocaria um problema por outro.
      */}
      <div
        role="status"
        className="flex flex-wrap items-center justify-between gap-2 bg-brand-blue-soft px-4 py-2 text-sm text-brand-blue"
      >
        <span>
          {conflitos.length === 1
            ? 'Uma alteração sua foi editada em outro aparelho.'
            : `${conflitos.length} alterações suas foram editadas em outro aparelho.`}
        </span>

        <button
          ref={abridorRef}
          type="button"
          onClick={() => setAberto(true)}
          className="min-h-touch rounded-sm bg-surface px-3 font-medium"
        >
          Escolher versão
        </button>
      </div>

      {aberto ? (
        <Dialogo
          conflitos={conflitos}
          onFechar={() => {
            setAberto(false);
            // O foco volta a quem abriu: fechar sem devolver o foco joga o
            // leitor de tela para o inicio do documento.
            abridorRef.current?.focus();
          }}
          onManterMinha={onManterMinha}
          onDescartarMinha={onDescartarMinha}
        />
      ) : null}
    </>
  );
}

function Dialogo({
  conflitos,
  onFechar,
  onManterMinha,
  onDescartarMinha,
}: ConflictPanelProps & { onFechar: () => void }) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-brand-navy/40 p-4 pt-[8vh]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onFechar();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Conflitos de sincronização"
        onKeyDown={(e) => {
          if (e.key === 'Escape') onFechar();
        }}
        className="flex max-h-[80dvh] w-full max-w-lg flex-col overflow-y-auto rounded bg-surface p-4 shadow-card"
      >
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-display text-md font-semibold">Escolher versão</h2>
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar"
            className="min-h-touch rounded-sm px-2 text-text-mut hover:bg-surface-2"
          >
            ✕
          </button>
        </div>

        <p className="mb-4 text-sm text-text-mut">
          Estas alterações foram feitas sem rede e, enquanto isso, a mesma tarefa mudou em
          outro aparelho. Nada foi perdido — escolha qual versão vale.
        </p>

        <ul className="flex flex-col gap-4">
          {conflitos.map((conflito) => (
            <Conflito
              key={conflito.opId}
              conflito={conflito}
              onManterMinha={() => onManterMinha(conflito)}
              onDescartarMinha={() => onDescartarMinha(conflito)}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function Conflito({
  conflito,
  onManterMinha,
  onDescartarMinha,
}: {
  conflito: SyncConflict;
  onManterMinha: () => void;
  onDescartarMinha: () => void;
}) {
  const timeZone = useTimeZone();
  const [confirmandoDescarte, setConfirmandoDescarte] = useState(false);

  const titulo = conflito.theirs?.title ?? 'Tarefa removida';
  const campos = camposEmConflito(conflito, timeZone);

  return (
    <li className="rounded border border-border p-3">
      <p className="font-medium">{titulo}</p>
      <p className="mt-0.5 text-xs text-text-mut">
        Sua alteração é de {quando(conflito.queuedAt, timeZone)}; a do servidor, de{' '}
        {quando(conflito.serverUpdatedAt, timeZone)}.
      </p>

      {/*
        So os campos que DIFEREM. Mostrar o registro inteiro faria procurar a
        diferenca a olho — e a diferenca e justamente a unica coisa sobre a qual
        ha uma decisao a tomar.
      */}
      <dl className="mt-3 flex flex-col gap-2 text-sm">
        {campos.map((campo) => (
          <div key={campo.nome} className="grid grid-cols-[6rem_1fr] gap-x-2 gap-y-0.5">
            <dt className="text-text-mut">{campo.rotulo}</dt>
            <dd className="flex flex-col gap-0.5">
              <span>
                <strong className="font-medium">A sua:</strong> {campo.minha}
              </span>
              <span className="text-text-mut">
                <strong className="font-medium">No servidor:</strong> {campo.deles}
              </span>
            </dd>
          </div>
        ))}
      </dl>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onManterMinha}
          className="min-h-touch flex-1 rounded bg-brand-blue px-3 font-medium text-white"
        >
          Manter a minha
        </button>

        {/*
          Descartar PERGUNTA, e e a segunda excecao a convencao 6 ("desfazer,
          nao confirmar") — pelo mesmo criterio da primeira: aqui o desfazer nao
          teria o que devolver. A escrita sai da fila e deixa de existir em
          qualquer lugar; nao ha endpoint, cache ou historico de onde traze-la
          de volta. Confirmar por precaucao generica continua proibido.
        */}
        {confirmandoDescarte ? (
          <button
            type="button"
            onClick={onDescartarMinha}
            className="min-h-touch flex-1 rounded bg-overdue px-3 font-medium text-white"
          >
            Descartar mesmo assim
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmandoDescarte(true)}
            className="min-h-touch flex-1 rounded bg-surface-2 px-3 font-medium"
          >
            Ficar com a do servidor
          </button>
        )}
      </div>

      {confirmandoDescarte ? (
        <p className="mt-2 text-xs text-text-mut">
          Sua alteração será apagada e não há como recuperá-la.{' '}
          <button
            type="button"
            onClick={() => setConfirmandoDescarte(false)}
            className="underline"
          >
            Cancelar
          </button>
        </p>
      ) : null}
    </li>
  );
}

interface CampoEmConflito {
  nome: string;
  rotulo: string;
  minha: string;
  deles: string;
}

/** Rotulos dos campos que uma operacao de tarefa pode carregar. */
const ROTULOS: Record<string, string> = {
  title: 'Título',
  notes: 'Notas',
  dueAt: 'Prazo',
  priority: 'Prioridade',
  listId: 'Lista',
  tagIds: 'Tags',
  reminderAt: 'Lembrete',
  status: 'Situação',
  quadrant: 'Quadrante',
  allDay: 'Dia inteiro',
  deleted: 'Excluída',
};

const PRIORIDADES = ['Nenhuma', 'Baixa', 'Média', 'Alta'];

/**
 * Os campos que a operacao queria gravar e que estao diferentes no servidor.
 *
 * `mine` e o patch — so os campos que mudaram —, e `theirs` e a linha inteira.
 * Comparar campo a campo evita listar como conflito aquilo em que as duas
 * versoes ja concordam: se a pessoa mudou o titulo e o outro aparelho mudou o
 * prazo, nao ha o que escolher no titulo.
 */
function camposEmConflito(conflito: SyncConflict, timeZone: string): CampoEmConflito[] {
  const theirs = conflito.theirs;

  return Object.entries(conflito.mine)
    .map(([nome, valorMeu]) => {
      // `deleted: true` e `status: 'done'` nao sao campos do patch: sao como o
      // servidor descreve uma exclusao e uma conclusao vindas da fila.
      const valorDeles =
        nome === 'deleted'
          ? theirs?.deletedAt !== null && theirs !== null
          : ((theirs ?? {}) as Record<string, unknown>)[nome];

      return {
        nome,
        rotulo: ROTULOS[nome] ?? nome,
        minha: mostrar(nome, valorMeu, timeZone),
        deles: mostrar(nome, valorDeles, timeZone),
      };
    })
    .filter((campo) => campo.minha !== campo.deles);
}

function mostrar(nome: string, valor: unknown, timeZone: string): string {
  if (valor === null || valor === undefined || valor === '') return '(vazio)';
  if (nome === 'dueAt' || nome === 'reminderAt') {
    return typeof valor === 'number' ? quando(valor, timeZone) : String(valor);
  }
  if (nome === 'priority') return PRIORIDADES[Number(valor)] ?? String(valor);
  if (nome === 'status') return valor === 'done' ? 'Concluída' : 'Em aberto';
  if (nome === 'deleted') return valor === true ? 'Sim' : 'Não';
  if (Array.isArray(valor)) return valor.length === 0 ? '(nenhuma)' : `${valor.length}`;
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  return String(valor);
}
